import { Injectable } from '@angular/core';
import { Adapter } from './adapter';

export class User {
    _status: string;
    _created: Date;
    _modified: Date;
    _username: string;
    _password: string;
    _attributes: { [name: string]: string };
    _isEnabled: boolean = false;
    _isAdmin: boolean = false;
    _isDevOnly: boolean = false;
    _isMfaEnabled: boolean = false;

    constructor(username: string, password: string, status: string,
        enabled: boolean, created: Date, modified: Date,
        isAdmin: boolean, isDev: boolean, attributes: { [name: string]: string },
        isMfaEnabled: boolean = false)
    {
        this._username = username;
        this._password = password;

        this._isEnabled = enabled;
        this._isAdmin = isAdmin;
        this._isDevOnly = isDev;

        // Strip "custom:" prefix from custom attributes, if present
        this._attributes = Object.fromEntries(
            Object.entries(attributes).map(([k, v]) => [k.replace('custom:', ''), v])
        );

        this._isMfaEnabled = isMfaEnabled;

        this._status = status;
        this._created = created;
        this._modified = modified;
    }

    /* ID */
    get id(): string {
        return this._attributes?.sub || '';
    }

    /* Username */
    get username(): string {
        return this._username;
    }
    set username(value: string) {
        this._username = value;
    }

    /* Password */
    get password(): string {
        return this._password;
    }
    set password(value: string) {
        this._password = value;
    }

    /* Name */
    get name(): string {
        return this._attributes?.name || '';
    }
    set name(value: string) {
        this._attributes.name = value;
    }
    get firstName(): string {
        return this.name?.split(' ')[0] || '';
    }
    get lastName(): string {
        return this.name?.split(' ')[1] || '';
    }

    /* Email */
    get email(): string {
        return this._attributes?.email || '';
    }
    set email(value: string) {
        this._attributes.email = value;
    }

    /* Phone */
    get phone(): string {
        return this._attributes?.phone_number || '';
    }
    set phone(value: string) {
        this._attributes.phone_number = value;
    }

    /* Title */
    get title(): string {
        return this._attributes?.title || '';
    }
    set title(value: string) {
        this._attributes.title = value;
    }

    /* Status */
    get status(): string {
        return this._status;
    }

    /* Created Date */
    get created(): Date {
        return this._created;
    }

    /* Last Modified Date */
    get modified(): Date {
        return this._modified;
    }

    /* Is User Enabled */
    get enabled(): boolean {
        return this._isEnabled;
    }
    set enabled(value: boolean) {
        this._isEnabled = value;
    }

    /* Is User Admin */
    get admin(): boolean {
        return this._isAdmin;
    }
    set admin(value: boolean) {
        this._isAdmin = value;
    }

    /* Is User Dev Only */
    get dev(): boolean {
        return this._isDevOnly;
    }
    set dev(value: boolean) {
        this._isDevOnly = value;
    }

    /* Has MFA Enabled */
    get mfaEnabled(): boolean {
        return this._isMfaEnabled;
    }
    set mfaEnabled(value: boolean) {
        this._isMfaEnabled = value;
    }

    get emailVerified(): boolean {
        return this._attributes?.['email_verified'] === 'true';
    }

    get phoneVerified(): boolean {
        return this._attributes?.['phone_number_verified'] === 'true';
    }
}

@Injectable()
export class UserAdapter implements Adapter<User> {

    private static arrayToObject = (array: any) =>
      array.reduce((obj: any, item: any) => {
        if (item.Name.startsWith('custom:')) {
            // Strip "custom:" prefix from custom attributes
            obj[item['Name'].substring(7)] = item['Value'];
        } else {
            obj[item['Name']] = item['Value'];
        }
        return obj;
      }, {});

    /* Attributes may be provided as "Attributes" or "UserAttributes" */
    adapt(item: any): User {
        const attributes = item.Attributes || item.attributes || item.UserAttributes || item.userAttributes || {};
        const mfaSettings: string[] = item.UserMFASettingList || item.userMFASettingList || [];
        const isMfaEnabled = mfaSettings.includes('SOFTWARE_TOKEN_MFA');
        return new User(
            item.Username || item.username || '',
            item.Password || item.password || null,
            item.UserStatus || item.userStatus || '',
            item.Enabled || item.enabled || null,
            item.UserCreateDate || item.userCreateDate || null,
            item.UserLastModifiedDate || item.userLastModifiedDate || null,
            false, // Admin flag not provided here
            false, // Dev flag not provided here
            (attributes instanceof Array) ? UserAdapter.arrayToObject(attributes) : attributes,
            isMfaEnabled
        );
    }

}
